import React, { useEffect, useState } from "react";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { Card } from "@/hq/HQPrimitives";
import { useHQAuth } from "@/hq/HQAuthContext";
import { useTestMode } from "@/hq/TestModeContext";
import { toast } from "sonner";

/**
 * HQTestModePanel — owner-only control for the backend Test Mode toggle
 * (Phase 3I.3I safety engine). Consumes:
 *
 *   GET  /api/hq/settings/test-mode
 *   POST /api/hq/settings/test-mode
 *
 * Non-owner roles see the current state read-only. Enabling refuses
 * unless a Test Recipient email is configured — surfaced as friendly
 * inline error text.
 */
export default function HQTestModePanel() {
  const { admin } = useHQAuth();
  const { refresh: refreshBanner } = useTestMode();
  const isOwner = (admin?.role || "").toLowerCase() === "owner";
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailDraft, setEmailDraft] = useState("");
  const [inlineErr, setInlineErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await hqApi.get("/settings/test-mode");
      setState(r.data);
      setEmailDraft(r.data.test_recipient_email || "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load Test Mode");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    setInlineErr("");
    try {
      const r = await hqApi.post("/settings/test-mode", patch);
      setState(r.data);
      setEmailDraft(r.data.test_recipient_email || "");
      refreshBanner();
      toast.success("Test Mode updated");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "test_recipient_email_required") {
        setInlineErr("Enter a test recipient email before enabling Test Mode.");
      } else {
        toast.error(formatApiError(d) || "Save failed");
      }
    }
    setSaving(false);
  };

  if (loading || !state) {
    return <Card className="p-6" data-testid="hq-test-mode-panel-loading">Loading Test Mode…</Card>;
  }

  const on = !!state.test_mode;
  return (
    <Card className="p-6" data-testid="hq-test-mode-panel">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#FF6A00]">Test Mode</div>
          <h3 className="mt-1 text-xl font-black tracking-tight" data-testid="hq-test-mode-title">
            {on ? "🧪 TEST MODE ACTIVE" : "LIVE MODE"}
          </h3>
          <p className="mt-2 text-sm text-[#4A4A4A] max-w-[520px]" data-testid="hq-test-mode-safety-copy">
            {on
              ? "TEST MODE is active. Live payment provider calls are blocked and outbound emails are redirected to the configured test recipient. Test payments do not count towards live revenue."
              : "Live payment and email behaviour is active."}
          </p>
        </div>
        <div className="shrink-0" data-testid="hq-test-mode-toggle-wrap">
          <button
            type="button"
            disabled={!isOwner || saving}
            onClick={() => save({ test_mode: !on })}
            data-testid={on ? "hq-test-mode-disable" : "hq-test-mode-enable"}
            className={`px-5 py-3 rounded-full text-[11px] font-black tracking-[0.16em] uppercase transition-colors ${
              on ? "bg-[#0A0A0A] text-white hover:bg-[#2A2A2A]"
                 : "bg-[#FF6A00] text-black hover:brightness-95"
            } ${(!isOwner || saving) ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {on ? "Disable Test Mode" : "Enable Test Mode"}
          </button>
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-[1fr_auto] items-end">
        <label className="block">
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#4A4A4A]">Test recipient email</div>
          <input
            type="email"
            value={emailDraft}
            onChange={(e) => setEmailDraft(e.target.value)}
            disabled={!isOwner || saving}
            placeholder="test@yourdomain.com"
            data-testid="hq-test-recipient-input"
            className="mt-1 w-full h-11 px-3 rounded-lg border border-[#EAEAEA] focus:border-[#FF6A00] focus:outline-none text-sm"
          />
        </label>
        <button
          type="button"
          disabled={!isOwner || saving || emailDraft === (state.test_recipient_email || "")}
          onClick={() => save({ test_recipient_email: emailDraft })}
          data-testid="hq-test-recipient-save"
          className="h-11 px-5 rounded-full bg-[#0A0A0A] text-white text-[11px] font-black tracking-[0.16em] uppercase disabled:opacity-40"
        >
          Save recipient
        </button>
      </div>
      {inlineErr && (
        <div className="mt-3 text-[13px] text-[#C1350B] font-medium"
              data-testid="hq-test-mode-inline-error">{inlineErr}</div>
      )}
      {!isOwner && (
        <div className="mt-3 text-[12px] text-[#8A8A8A]"
              data-testid="hq-test-mode-readonly-notice">
          Only OWNER role may change Test Mode.
        </div>
      )}
      <div className="mt-4 text-[11px] tracking-[0.16em] uppercase text-[#8A8A8A]"
            data-testid="hq-test-mode-status-line">
        Status: <span className="font-black text-[#0A0A0A]">{on ? "ON" : "OFF"}</span>
        {state.updated_at ? ` · updated ${new Date(state.updated_at).toLocaleString()}` : ""}
        {state.updated_by ? ` · by ${state.updated_by}` : ""}
      </div>
    </Card>
  );
}
